import React, { useCallback } from "react";
import type { FormInstance } from "antd";
import { Form, Input, Select } from "../../../../shared";
import { formatWholeNumberInput } from "../../../../CommonComponents/FormattedInput/FormattedInput";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formFieldTooltip } from "./formFieldTooltip";
import {
  AWARD_FREQUENCY,
  DEFAULT_AWARD_FREQUENCY,
  DEFAULT_CUSTOM_FREQUENCY_AMOUNT,
  DEFAULT_CUSTOM_FREQUENCY_UNIT,
  type AwardFrequency,
  type AwardFrequencyUnit,
} from "./awardFrequency";
import {
  validateMaxAwardsPerUser,
  type WizardFormValues,
} from "./toWizardCampaignIn";
import type { DynamicBonusCatalog, ResultType } from "../../types";
import styles from "../../stylesDynamicBonuses.module.css";

const frequencyOptions = (): Array<{ value: AwardFrequency; label: string }> => [
  {
    value: AWARD_FREQUENCY.ONE_TIME,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_ONE_TIME,
  },
  {
    value: AWARD_FREQUENCY.DAILY,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_DAILY,
  },
  {
    value: AWARD_FREQUENCY.WEEKLY,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_WEEKLY,
  },
  {
    value: AWARD_FREQUENCY.MONTHLY,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_MONTHLY,
  },
  {
    value: AWARD_FREQUENCY.CUSTOM,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_CUSTOM,
  },
  {
    value: AWARD_FREQUENCY.NO_LIMIT,
    label: AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_NO_LIMIT,
  },
];

const unitOptions = (): Array<{ value: AwardFrequencyUnit; label: string }> => [
  { value: "hours", label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_HOURS },
  { value: "days", label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_DAYS },
];

type Props = {
  form: FormInstance<WizardFormValues>;
  disabled?: boolean;
  catalog?: DynamicBonusCatalog;
};

/**
 * Campaign-level Award Frequency select. Compiles to caps +
 * `cooldown_since_last_award` on submit — see `compileAwardFrequency`.
 */
const AwardFrequencyField: React.FC<Props> = ({
  form,
  disabled = false,
  catalog,
}) => {
  const frequency =
    (Form.useWatch("award_frequency", form) as AwardFrequency | undefined) ??
    DEFAULT_AWARD_FREQUENCY;
  const showCustom = frequency === AWARD_FREQUENCY.CUSTOM;
  const showCap = frequency !== AWARD_FREQUENCY.ONE_TIME;
  const showRollingHint =
    frequency === AWARD_FREQUENCY.DAILY ||
    frequency === AWARD_FREQUENCY.WEEKLY ||
    frequency === AWARD_FREQUENCY.MONTHLY;

  const handleFrequencyChange = useCallback(
    (next: AwardFrequency) => {
      form.setFieldValue("award_frequency_exact_seconds", undefined);
      if (next !== AWARD_FREQUENCY.CUSTOM) return;
      if (!form.getFieldValue("award_frequency_amount")) {
        form.setFieldValue(
          "award_frequency_amount",
          DEFAULT_CUSTOM_FREQUENCY_AMOUNT,
        );
      }
      if (!form.getFieldValue("award_frequency_unit")) {
        form.setFieldValue(
          "award_frequency_unit",
          DEFAULT_CUSTOM_FREQUENCY_UNIT,
        );
      }
    },
    [form],
  );

  const clearExactSeconds = useCallback(() => {
    form.setFieldValue("award_frequency_exact_seconds", undefined);
  }, [form]);

  return (
    <>
      <Form.Item name="award_frequency_exact_seconds" hidden>
        <Input type="hidden" data-testid="award-frequency-exact-seconds" />
      </Form.Item>
      <div className={styles.formFieldRow}>
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY}
          tooltip={formFieldTooltip(
            AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_TOOLTIP,
          )}
          name="award_frequency"
        >
          <Select
            options={frequencyOptions()}
            disabled={disabled}
            aria-label={AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY}
            onChange={(value) => handleFrequencyChange(value as AwardFrequency)}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
        {showCap && (
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_PER_USER}
            tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_TOOLTIP)}
            name="max_awards_per_user"
            dependencies={["steps"]}
            getValueFromEvent={(event) =>
              formatWholeNumberInput(event.target.value)
            }
            rules={[
              {
                validator: (_: unknown, value?: string) => {
                  const resolveResultType = (typeKey: string): ResultType | "" =>
                    catalog?.results?.find((r) => r.type_key === typeKey)
                      ?.result_type ?? "";
                  const message = validateMaxAwardsPerUser(
                    value,
                    form.getFieldValue("steps"),
                    resolveResultType,
                  );
                  return message
                    ? Promise.reject(new Error(message))
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_PLACEHOLDER}
              disabled={disabled}
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_PER_USER}
            />
          </Form.Item>
        )}
      </div>
      {showCustom && (
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_CUSTOM_AMOUNT}
          required
          className={styles.awardFrequencyCustomItem}
        >
          <div className={styles.runForControl} role="group">
            <Form.Item
              name="award_frequency_amount"
              noStyle
              rules={[
                {
                  required: true,
                  message:
                    AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_CUSTOM_REQUIRED,
                },
              ]}
              getValueFromEvent={(event) => {
                clearExactSeconds();
                return formatWholeNumberInput(event.target.value);
              }}
            >
              <Input
                className={styles.awardFrequencyAmount}
                disabled={disabled}
                aria-label={
                  AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_CUSTOM_AMOUNT
                }
              />
            </Form.Item>
            <Form.Item name="award_frequency_unit" noStyle>
              <Select
                className={styles.awardFrequencyUnit}
                options={unitOptions()}
                disabled={disabled}
                aria-label={
                  AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_CUSTOM_UNIT
                }
                popupMatchSelectWidth={false}
                onChange={clearExactSeconds}
              />
            </Form.Item>
          </div>
        </Form.Item>
      )}
      {showRollingHint && (
        <p className={styles.stepHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_AWARD_FREQUENCY_HINT}
        </p>
      )}
    </>
  );
};

export default AwardFrequencyField;
